/**
 * Functions to handle loading a conversation from the server
 */
import { QuestionObject, Source } from './types';

/**
 * Interface for conversation data
 */
export interface ConversationData {
  question: string;
  answer: string;
  sources: Source[];
}

/**
 * Interface for chat state required for loading conversations
 */
export interface ChatStateForLoading {
  setLastQuestion: (question: string) => void;
  setCurrentAnswer: (answer: string) => void;
  setAnswerSources: (sources: Source[]) => void;
  lastQuestionID: { current: string };
  addItemToHistory: (speaker: 'user' | 'chatbot', content: string) => void;
}

/**
 * Parse URL parameters to find a conversation ID
 */
export function getConversationIdFromUrl(): string | null {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('conversation');
  }
  return null;
}

/**
 * Fetch a conversation from the server by ID
 */
export async function fetchConversation(
  conversationId: string,
  serverUrl: string,
): Promise<ConversationData | null> {
  try {
    // Convert WebSocket URL to HTTP/HTTPS
    const url = new URL(serverUrl);
    const isLocalhost = url.hostname === 'localhost';

    // Replace wss:// with https:// (or http:// for localhost)
    const httpProtocol = isLocalhost ? 'http:' : 'https:';
    url.protocol = httpProtocol;
    const apiUrl = `${url.origin}/conversation/${conversationId}`;
    const response = await fetch(apiUrl);

    if (!response.ok) {
      throw new Error(`Error fetching conversation: ${response.status}`);
    }

    const data = await response.json();

    return {
      question: data.question || '',
      answer: data.answer || '',
      sources: data.context || [],
    };
  } catch (error) {
    console.error('Error fetching conversation:', error);
    return null;
  }
}

/**
 * Load a conversation into the chat state
 */
export function loadConversation(
  conversationData: ConversationData,
  chatState: ChatStateForLoading,
): void {
  if (conversationData) {
    chatState.setLastQuestion(conversationData.question);
    chatState.setCurrentAnswer(conversationData.answer);
    chatState.setAnswerSources(conversationData.sources);

    // Save conversation ID
    chatState.lastQuestionID.current = getConversationIdFromUrl() || '';

    // Add items to history
    chatState.addItemToHistory('user', conversationData.question);
    chatState.addItemToHistory('chatbot', conversationData.answer);
  }
}
